import type { RequestContext } from "../context/request-context.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { PermissionCode } from "../../domain/identity/access-types.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import type { JsonObject } from "../../shared/kernel/json.js";
import {
  DEFAULT_LOGIN,
  FONT_OPTIONS,
  HISTORY_AREA_OF_SECTION,
  LOGO_SLOTS,
  TEMPLATED_DOCUMENTS,
  THEME_CATALOG,
  isThemeCustomized,
  paletteOfTheme,
  themeById,
  themeContrastIssues,
  type ThemeDesign,
  type ThemePalette,
  changedSections,
  defaultVisualDesign,
  documentVisualOf,
  normalizeVisualDesign,
  studioWarnings,
  validateForPublish,
  validateStudioSection,
  type DocumentVisual,
  type LoginDesign,
  type LogoSlot,
  type StudioAsset,
  type StudioHistoryEntry,
  type StudioSectionId,
  type TemplatedDocument,
  type VisualDesign
} from "../../domain/studio/visual-design.js";
import type { StudioDesignRepository, StudioVersionSummary } from "../../repositories/studio-design-repository.js";
import { VISUAL_PRESETS, presetById, type PresetAssetKey } from "../../domain/studio/visual-presets.js";
import { sha256Hex, type DocumentStorageService } from "./document-storage-service.js";
import type { StudioDesignProvider } from "./studio-design-provider.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/** Leitura e edição de cada seção. Publicar e restaurar: studio.publish. Histórico: studio.audit.read. */
const SECTION_PERMISSIONS: Record<StudioSectionId, { read: PermissionCode; update: PermissionCode }> = {
  identity: { read: PermissionCode.StudioIdentityRead, update: PermissionCode.StudioIdentityUpdate },
  logos: { read: PermissionCode.StudioIdentityRead, update: PermissionCode.StudioIdentityUpdate },
  theme: { read: PermissionCode.StudioThemeRead, update: PermissionCode.StudioThemeUpdate },
  typography: { read: PermissionCode.StudioThemeRead, update: PermissionCode.StudioThemeUpdate },
  layout: { read: PermissionCode.StudioThemeRead, update: PermissionCode.StudioThemeUpdate },
  dashboard: { read: PermissionCode.StudioThemeRead, update: PermissionCode.StudioThemeUpdate },
  login: { read: PermissionCode.StudioRead, update: PermissionCode.StudioLoginUpdate },
  documents: { read: PermissionCode.StudioDocumentsRead, update: PermissionCode.StudioDocumentsUpdate },
  signature: { read: PermissionCode.StudioDocumentsRead, update: PermissionCode.StudioDocumentsUpdate },
  templates: { read: PermissionCode.StudioTemplatesRead, update: PermissionCode.StudioTemplatesUpdate }
};

const ALL_SECTIONS = Object.keys(SECTION_PERMISSIONS) as StudioSectionId[];

export type StudioAssetTarget = { kind: "logo"; slot: LogoSlot } | { kind: "loginBackground" } | { kind: "loginBrandLogo" };

export interface PresetSummary {
  id: string;
  name: string;
  description: string;
  sections: StudioSectionId[];
  assets: Array<{ key: PresetAssetKey; url: string; fileName: string; label: string }>;
  referenceImage: string | null;
  highlights: string[];
  /** A pessoa tem a permissão de edição de TODAS as seções que o tema reescreve? */
  canApply: boolean;
}

export interface StudioView {
  published: { version: number; design: Partial<VisualDesign> };
  draft: { design: Partial<VisualDesign>; version: number; baseVersion: number; updatedAt: string | null; updatedBy: string | null };
  /** Seções com alteração ainda não publicada. */
  pending: StudioSectionId[];
  versions: StudioVersionSummary[];
  readable: StudioSectionId[];
  editable: StudioSectionId[];
  canPublish: boolean;
  publishErrors: string[];
  warnings: string[];
  /** ref → dataURL, só dos arquivos que o rascunho e a versão publicada usam (para as miniaturas). */
  assetUrls: Record<string, string>;
  /** Temas de cor do catálogo, com o status REAL (publicado × rascunho). */
  themes: ThemeCardView[];
  /** Resumo da tela inicial — só do que está persistido, nunca de padrão tomado como verdade. */
  overview: StudioOverview;
}

/** ATIVO = publicado; RASCUNHO = no rascunho, ainda não publicado; DISPONÍVEL = no catálogo. */
export type ThemeCardStatus = "ATIVO" | "RASCUNHO" | "DISPONIVEL";

export interface ThemeCardView {
  id: string;
  name: string;
  description: string;
  mode: ThemeDesign["mode"];
  palette: ThemePalette;
  status: ThemeCardStatus;
  /** Ativo com cores editadas depois de aplicado. */
  customized: boolean;
  /** Ativo e com alteração de tema pendente no rascunho. */
  draftPending: boolean;
  /** Versão publicada em que o tema vigente passou a valer (só para o ATIVO). */
  version: number | null;
  /** Última alteração: publicação (ATIVO) ou gravação do rascunho (RASCUNHO). */
  changedAt: string | null;
  changedBy: string | null;
  contrastIssues: string[];
}

export type OverviewStatus = "NAO_CONFIGURADO" | "CONFIGURADO" | "RASCUNHO" | "ATIVO" | "ERRO" | "SEM_PERMISSAO";

export interface StudioOverviewItem {
  status: OverviewStatus;
  /** Valor principal, já pronto para a tela. */
  value: string;
  detail: string;
  /** Há alteração desta área no rascunho, ainda não publicada. */
  pending: boolean;
}

export interface StudioOverview {
  publishedVersion: number;
  lastPublication: { at: string; by: string; reason: string } | null;
  draft: { pendingSections: StudioSectionId[]; updatedAt: string | null; updatedBy: string | null };
  identity: StudioOverviewItem;
  theme: StudioOverviewItem & { themeId: string | null };
  draftTheme: { themeId: string; name: string; customized: boolean } | null;
  logos: StudioOverviewItem;
  login: StudioOverviewItem;
  documents: StudioOverviewItem;
  templates: StudioOverviewItem & { published: number; drafts: number; total: number };
}

export interface AppAppearance {
  version: number;
  identity: VisualDesign["identity"];
  theme: VisualDesign["theme"];
  typography: VisualDesign["typography"];
  layout: VisualDesign["layout"];
  dashboard: VisualDesign["dashboard"];
  logos: { app: string | null; favicon: string | null; appIcon: string | null };
}

export interface LoginAppearance {
  version: number;
  login: Omit<LoginDesign, "backgroundImage" | "brandLogo">;
  backgroundImageUrl: string | null;
  loginLogoUrl: string | null;
  brandLogoUrl: string | null;
  /** Públicos por natureza: aparecem antes do login (aba do navegador e tela de abertura). */
  appLogoUrl: string | null;
  faviconUrl: string | null;
  appIconUrl: string | null;
  identity: { subtitle: string; versionLabel: string };
  /** Fonte publicada, explícita: o /login (sem sessão) e o preview (dentro do app) desenham igual. */
  fontStack: string;
}

const DATA_URL = /^data:(image\/(?:png|jpeg|webp));base64,([A-Za-z0-9+/=]+)$/;
/** Assinatura dos primeiros bytes: o tipo declarado precisa bater com o conteúdo. SVG não entra (pode carregar script). */
const MAGIC: Record<string, string> = { "image/png": "iVBORw0KGgo", "image/jpeg": "/9j/", "image/webp": "UklGR" };

/**
 * STUDIO INSPECT IA — fachada exposta às telas (e ao `/api/rpc` no modo
 * servidor). Toda edição vai para o RASCUNHO; nada muda no sistema ativo até
 * PUBLICAR. Publicar cria uma versão imutável; restaurar uma versão antiga
 * cria outra versão nova — o histórico nunca é reescrito.
 */
export class StudioDesignService {
  private readonly auth = new RbacAuthorizationService();
  private userNameOf: ((userId: string) => Promise<string | null>) | null = null;

  constructor(
    private readonly provider: StudioDesignProvider,
    private readonly repo: StudioDesignRepository,
    private readonly storage: DocumentStorageService,
    private readonly audit?: AuditRecorder
  ) {}

  attachUserNameResolver(resolver: (userId: string) => Promise<string | null>): void {
    this.userNameOf = resolver;
  }

  // ═══════════════════════════════════════════════════ aparência vigente

  /** O que a aplicação aplica em todas as telas — para qualquer sessão autenticada. */
  async getAppearance(context: RequestContext): Promise<AppAppearance> {
    if (!context.userId) throw new AppError(ErrorCode.Authentication, "Not authenticated", { safeMessage: "Sessão não autenticada." });
    const { design, version } = await this.provider.published();
    return {
      version, identity: design.identity, theme: design.theme, typography: design.typography, layout: design.layout, dashboard: design.dashboard,
      logos: { app: await this.provider.resolveAsset(design.logos.app), favicon: await this.provider.resolveAsset(design.logos.favicon), appIcon: await this.provider.resolveAsset(design.logos.appIcon) }
    };
  }

  /**
   * A tela de login PUBLICADA — pública por natureza (é o que qualquer
   * visitante vê antes de entrar). Só dados visuais do login; nada de
   * rascunho, histórico ou configuração.
   */
  async getPublicLoginAppearance(): Promise<LoginAppearance> {
    const { design, version } = await this.provider.published();
    return this.loginAppearanceOf(design, version);
  }

  // ═══════════════════════════════════════════════════════════ Studio

  async getStudio(context: RequestContext): Promise<StudioView> {
    this.auth.require(context, PermissionCode.StudioRead);
    const { design: published, version: publishedVersion } = await this.provider.published();
    const draftRecord = await this.repo.loadDraft();
    const draft = draftRecord ? normalizeVisualDesign(draftRecord.payload) : published;
    const readable = ALL_SECTIONS.filter(s => this.auth.can(context, SECTION_PERMISSIONS[s].read));
    const editable = readable.filter(s => this.auth.can(context, SECTION_PERMISSIONS[s].update));
    const pick = (d: VisualDesign) => Object.fromEntries(readable.map(s => [s, structuredClone(d[s])])) as Partial<VisualDesign>;
    const assets = new Map<string, StudioAsset>();
    for (const d of [draft, published]) {
      for (const s of LOGO_SLOTS) { const a = d.logos[s.slot]; if (a) assets.set(a.ref, a); }
      if (d.login.backgroundImage) assets.set(d.login.backgroundImage.ref, d.login.backgroundImage);
      if (d.login.brandLogo) assets.set(d.login.brandLogo.ref, d.login.brandLogo);
    }
    const assetUrls: Record<string, string> = {};
    for (const [ref, asset] of assets) { const url = await this.provider.resolveAsset(asset); if (url) assetUrls[ref] = url; }
    return {
      published: { version: publishedVersion, design: pick(published) },
      draft: { design: pick(draft), version: draftRecord?.version ?? 0, baseVersion: draftRecord?.baseVersion ?? publishedVersion, updatedAt: draftRecord?.updatedAt ?? null, updatedBy: draftRecord?.updatedBy ?? null },
      pending: changedSections(draft, published),
      versions: await this.repo.listVersions(),
      readable, editable,
      canPublish: this.auth.can(context, PermissionCode.StudioPublish),
      publishErrors: validateForPublish(draft),
      warnings: studioWarnings(draft),
      assetUrls,
      themes: await this.themeCards(context, published, draft, draftRecord),
      overview: await this.overviewOf(readable, published, publishedVersion, draft, draftRecord)
    };
  }

  // ═══════════════════════════════════════════════════════ temas de cor

  /**
   * APLICAR TEMA DO CATÁLOGO — escreve SÓ a seção Tema do RASCUNHO. O sistema
   * ativo não muda; para valer, alguém com studio.publish PUBLICA.
   */
  async applyTheme(context: RequestContext, themeId: string, expectedDraftVersion: number): Promise<StudioView> {
    this.auth.require(context, PermissionCode.StudioThemeUpdate);
    const entry = themeById(String(themeId ?? ""));
    if (!entry) throw new AppError(ErrorCode.NotFound, "Theme not found", { safeMessage: "Tema não encontrado no catálogo." });
    const { draft, record } = await this.draftForWrite(expectedDraftVersion);
    const theme: ThemeDesign = structuredClone(entry.theme);
    const errors = validateStudioSection("theme", theme);
    if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Invalid theme", { safeMessage: errors.join(" ") });
    if (JSON.stringify(theme) === JSON.stringify(draft.theme)) return this.getStudio(context);
    await this.writeDraft(context, { ...draft, theme }, record, [{ area: "Tema", action: "RASCUNHO_SALVO", detail: `Tema "${entry.name}" aplicado ao RASCUNHO. O sistema ativo não mudou.` }]);
    await this.themeEvent(context, "STUDIO_THEME_CREATED", theme.themeId, null, draft.theme, theme);
    return this.getStudio(context);
  }

  private async themeCards(context: RequestContext, published: VisualDesign, draft: VisualDesign, draftRecord: { updatedAt: string; updatedBy: string } | null): Promise<ThemeCardView[]> {
    if (!this.auth.can(context, PermissionCode.StudioThemeRead)) return [];
    const draftDiffers = JSON.stringify(draft.theme) !== JSON.stringify(published.theme);
    const since = await this.themeActiveSince(published);
    const who = async (id: string | null) => (id ? ((this.userNameOf ? await this.userNameOf(id).catch(() => null) : null) || id) : null);
    const cards: ThemeCardView[] = [];
    for (const entry of THEME_CATALOG) {
      const active = published.theme.themeId === entry.id;
      const inDraft = draftDiffers && draft.theme.themeId === entry.id;
      const shown = active ? published.theme : inDraft ? draft.theme : entry.theme;
      cards.push({
        id: entry.id, name: entry.name, description: entry.description, mode: shown.mode, palette: paletteOfTheme(shown),
        status: active ? "ATIVO" : inDraft ? "RASCUNHO" : "DISPONIVEL",
        customized: (active || inDraft) && isThemeCustomized(shown),
        draftPending: active && draftDiffers,
        version: active ? since.version : null,
        changedAt: active ? since.at : inDraft ? draftRecord?.updatedAt ?? null : null,
        changedBy: active ? since.by : inDraft ? await who(draftRecord?.updatedBy ?? null) : null,
        contrastIssues: themeContrastIssues(shown)
      });
    }
    return cards;
  }

  /** Em que versão o tema publicado de hoje passou a valer (anda para trás enquanto o tema for o mesmo). */
  private async themeActiveSince(published: VisualDesign): Promise<{ version: number | null; at: string | null; by: string | null }> {
    const versions = await this.repo.listVersions();
    const target = JSON.stringify(published.theme);
    let found: StudioVersionSummary | null = null;
    for (const summary of versions.slice(0, 50)) {
      const record = await this.repo.getVersion(summary.version);
      if (!record || JSON.stringify(normalizeVisualDesign(record.payload).theme) !== target) break;
      found = summary;
    }
    if (!found) return { version: versions.length === 0 ? 0 : null, at: null, by: null };
    return { version: found.version, at: found.publishedAt, by: found.publishedByName };
  }

  /**
   * RESUMO DA TELA INICIAL. Cada linha sai do que está GRAVADO: a versão
   * publicada (ou a V0 de fábrica, dita como tal) e o rascunho. Igual ao
   * padrão = NÃO CONFIGURADO; diferente = CONFIGURADO; publicado e vigente =
   * ATIVO; alteração só no rascunho = marcada como pendente. Área que a
   * pessoa não pode ler aparece como tal — nunca com um valor inventado.
   */
  private async overviewOf(readable: StudioSectionId[], published: VisualDesign, version: number, draft: VisualDesign, draftRecord: { updatedAt: string; updatedBy: string } | null): Promise<StudioOverview> {
    const def = defaultVisualDesign();
    const pendingSections = changedSections(draft, published);
    const can = (s: StudioSectionId) => readable.includes(s);
    const pending = (s: StudioSectionId) => pendingSections.includes(s);
    const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
    const noRead: StudioOverviewItem = { status: "SEM_PERMISSAO", value: "—", detail: "Sem permissão de leitura desta área.", pending: false };
    const latest = (await this.repo.listVersions())[0] ?? null;
    const name = async (id: string | null) => (id ? ((this.userNameOf ? await this.userNameOf(id).catch(() => null) : null) || id) : null);

    const themeEntry = themeById(published.theme.themeId);
    const themeErrors = validateStudioSection("theme", published.theme);
    const draftThemeEntry = themeById(draft.theme.themeId);
    const logosCount = LOGO_SLOTS.filter(slot => published.logos[slot.slot]).length;
    const templateStatuses = TEMPLATED_DOCUMENTS.map(t => published.templates[t].status);
    const publishedTemplates = templateStatuses.filter(st => st === "PUBLICADO").length;
    const draftTemplates = TEMPLATED_DOCUMENTS.filter(t => draft.templates[t].status !== published.templates[t].status || draft.templates[t].status === "EM_EDICAO").length;

    return {
      publishedVersion: version,
      lastPublication: latest ? { at: latest.publishedAt, by: latest.publishedByName, reason: latest.reason } : null,
      draft: { pendingSections, updatedAt: draftRecord?.updatedAt ?? null, updatedBy: await name(draftRecord?.updatedBy ?? null) },
      identity: can("identity") ? {
        status: same(published.identity, def.identity) ? "NAO_CONFIGURADO" : "CONFIGURADO",
        value: published.identity.softwareName,
        detail: published.identity.subtitle,
        pending: pending("identity")
      } : noRead,
      theme: can("theme") ? {
        status: themeErrors.length > 0 ? "ERRO" : "ATIVO",
        value: `${themeEntry?.name ?? "Tema desconhecido"}${isThemeCustomized(published.theme) ? " (personalizado)" : ""}`,
        detail: themeErrors.length > 0 ? themeErrors.join(" ") : `Modo ${published.theme.mode === "ESCURO" ? "escuro" : published.theme.mode === "CLARO" ? "claro" : "automático"} · V${version}${version === 0 ? " (padrão de fábrica)" : ""}`,
        pending: pending("theme"),
        themeId: published.theme.themeId
      } : { ...noRead, themeId: null },
      draftTheme: can("theme") && pending("theme") ? { themeId: draft.theme.themeId, name: draftThemeEntry?.name ?? "Tema desconhecido", customized: isThemeCustomized(draft.theme) } : null,
      logos: can("logos") ? {
        status: logosCount > 0 ? "CONFIGURADO" : "NAO_CONFIGURADO",
        value: logosCount > 0 ? `${logosCount} de ${LOGO_SLOTS.length} enviados` : "Texto INSPECT IA",
        detail: logosCount > 0 ? "Logos publicados no armazenamento oficial." : "Nenhum logo enviado — vale o texto INSPECT IA.",
        pending: pending("logos")
      } : noRead,
      login: can("login") ? {
        status: same(published.login, DEFAULT_LOGIN) ? "NAO_CONFIGURADO" : "CONFIGURADO",
        value: published.login.variant === "CLARO" ? "Variante clara" : "Variante escura",
        detail: same(published.login, DEFAULT_LOGIN) ? "Tela de login de fábrica." : `Título "${published.login.title}"${published.login.backgroundImage ? " · imagem de fundo própria" : ""}.`,
        pending: pending("login")
      } : noRead,
      documents: can("documents") ? {
        status: same(published.documents, def.documents) ? "NAO_CONFIGURADO" : "CONFIGURADO",
        value: published.documents.logoMode === "IMAGEM" ? "Logo em imagem" : "Texto INSPECT IA",
        detail: same(published.documents, def.documents) ? "Cabeçalho documental INSPECT IA padrão." : "Cabeçalho documental personalizado (QR, cores e rodapé).",
        pending: pending("documents")
      } : noRead,
      templates: can("templates") ? {
        // Os 8 tipos já nascem PUBLICADOS no desenho de fábrica: igual ao padrão não é "configurado".
        status: same(published.templates, def.templates) ? "NAO_CONFIGURADO" : "CONFIGURADO",
        value: `${publishedTemplates} publicado(s)`,
        detail: `${TEMPLATED_DOCUMENTS.length} tipos de documento · ${draftTemplates} com alteração no rascunho`,
        pending: pending("templates"),
        published: publishedTemplates, drafts: draftTemplates, total: TEMPLATED_DOCUMENTS.length
      } : { ...noRead, published: 0, drafts: 0, total: TEMPLATED_DOCUMENTS.length }
    };
  }

  /** Auditoria do TEMA: id, versão, usuário, instante, antes e depois. */
  private async themeEvent(context: RequestContext, event: string, themeId: string, versionId: number | null, before: ThemeDesign, after: ThemeDesign): Promise<void> {
    await this.auditEvent(context, event, { themeId, versionId, userId: context.userId || "system", timestamp: new Date().toISOString(), before: { ...before }, after: { ...after } });
  }

  /** Uma versão publicada inteira (para visualizar/comparar antes de restaurar). */
  async getVersionDesign(context: RequestContext, version: number): Promise<VisualDesign> {
    this.auth.require(context, PermissionCode.StudioRead);
    if (version === 0) return defaultVisualDesign();
    const record = await this.repo.getVersion(version);
    if (!record) throw new AppError(ErrorCode.NotFound, "Version not found", { safeMessage: `Versão V${version} não encontrada.` });
    return normalizeVisualDesign(record.payload);
  }

  /** Login do RASCUNHO (ou de uma versão) para o VISUALIZAR LOGIN — antes de publicar. */
  async getLoginPreview(context: RequestContext, source: "DRAFT" | number = "DRAFT"): Promise<LoginAppearance> {
    this.auth.require(context, PermissionCode.StudioRead);
    const design = source === "DRAFT" ? await this.draftDesign() : await this.getVersionDesign(context, source);
    return this.loginAppearanceOf(design, source === "DRAFT" ? -1 : source);
  }

  /** Visual documental do RASCUNHO para o VISUALIZAR RELATÓRIO — o mesmo objeto que o PDF recebe. */
  async previewDocumentVisual(context: RequestContext, type: TemplatedDocument, source: "DRAFT" | "PUBLISHED" = "DRAFT"): Promise<DocumentVisual> {
    this.auth.require(context, PermissionCode.StudioDocumentsRead);
    const { design: published, version } = await this.provider.published();
    const design = source === "DRAFT" ? await this.draftDesign() : published;
    const logo = design.documents.logoMode === "IMAGEM" ? await this.provider.resolveAsset(design.logos.document) : null;
    return documentVisualOf(design, type, source === "DRAFT" ? -1 : version, logo);
  }

  // ═══════════════════════════════════════════════════════ temas salvos

  /** Temas salvos no sistema (nenhum aplicado por conta própria). */
  async listPresets(context: RequestContext): Promise<PresetSummary[]> {
    this.auth.require(context, PermissionCode.StudioRead);
    return VISUAL_PRESETS.map(p => ({
      id: p.id, name: p.name, description: p.description, sections: [...p.sections],
      assets: p.assets.map(a => ({ ...a })), referenceImage: p.referenceImage, highlights: [...p.highlights],
      canApply: p.sections.every(s => this.auth.can(context, SECTION_PERMISSIONS[s].update))
    }));
  }

  /**
   * APLICAR TEMA — escreve no RASCUNHO, nunca no sistema ativo. Exige a
   * permissão de edição de cada seção que o tema reescreve; os arquivos do
   * tema passam pela mesma validação de qualquer envio e vão para o
   * armazenamento oficial. Para valer, alguém com studio.publish PUBLICA.
   */
  async applyPreset(context: RequestContext, presetId: string, assetData: Record<string, string>, expectedDraftVersion: number): Promise<StudioView> {
    this.auth.require(context, PermissionCode.StudioRead);
    const preset = presetById(presetId);
    if (!preset) throw new AppError(ErrorCode.NotFound, "Preset not found", { safeMessage: "Tema salvo não encontrado." });
    for (const s of preset.sections) this.auth.require(context, SECTION_PERMISSIONS[s].update);
    const { draft, record } = await this.draftForWrite(expectedDraftVersion);
    const stored: Partial<Record<PresetAssetKey, StudioAsset>> = {};
    for (const a of preset.assets) {
      const data = assetData?.[a.key];
      if (!data) throw new AppError(ErrorCode.Validation, "Preset asset missing", { safeMessage: `O arquivo "${a.label}" do tema não foi enviado.` });
      const target: StudioAssetTarget = a.key === "documentLogo" ? { kind: "logo", slot: "document" } : a.key === "loginLogo" ? { kind: "logo", slot: "login" } : { kind: "loginBrandLogo" };
      stored[a.key] = await this.storeImage(context, data, a.fileName, this.assetRules(target));
    }
    const design = normalizeVisualDesign(preset.apply(draft, stored));
    for (const s of preset.sections) {
      const errors = validateStudioSection(s, design[s]);
      if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Invalid preset", { safeMessage: errors.join(" ") });
    }
    await this.writeDraft(context, design, record, [{ area: "Tema salvo", action: "RASCUNHO_SALVO", detail: `Tema "${preset.name}" aplicado ao RASCUNHO (${preset.sections.map(s => HISTORY_AREA_OF_SECTION[s]).join(", ")}). O sistema ativo não mudou.` }]);
    return this.getStudio(context);
  }

  /**
   * Visual de um documento que NÃO guarda modelo congelado (RI): pela versão
   * carimbada na aprovação — nunca pela versão de hoje num documento fechado.
   * Qualquer sessão que já está abrindo o documento pode pedir.
   */
  async resolveDocumentVisual(context: RequestContext, type: TemplatedDocument, documentId: string, closed: boolean): Promise<DocumentVisual> {
    if (!context.userId) throw new AppError(ErrorCode.Authentication, "Not authenticated", { safeMessage: "Sessão não autenticada." });
    return this.provider.visualForDocument(type, String(documentId), !!closed);
  }

  async listHistory(context: RequestContext, limit = 500): Promise<StudioHistoryEntry[]> {
    this.auth.require(context, PermissionCode.StudioAuditRead);
    return this.repo.listHistory(Math.min(Math.max(1, limit), 2000));
  }

  // ═══════════════════════════════════════════════════════════ rascunho

  async saveDraftSection(context: RequestContext, section: StudioSectionId, value: unknown, expectedDraftVersion: number): Promise<StudioView> {
    const permissions = SECTION_PERMISSIONS[section];
    if (!permissions) throw new AppError(ErrorCode.Validation, "Unknown section", { safeMessage: "Seção do Studio desconhecida." });
    this.auth.require(context, permissions.update);
    const { draft, record } = await this.draftForWrite(expectedDraftVersion);
    // Arquivos só entram pela ação de envio (validada); por aqui, só se mantém o que já está no rascunho.
    let next = normalizeVisualDesign({ ...draft, [section]: value })[section];
    if (section === "logos") next = draft.logos;
    if (section === "login") next = { ...(next as LoginDesign), backgroundImage: draft.login.backgroundImage, brandLogo: draft.login.brandLogo };
    const errors = validateStudioSection(section, next);
    if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Invalid studio section", { safeMessage: errors.join(" ") });
    if (JSON.stringify(next) === JSON.stringify(draft[section])) return this.getStudio(context);
    const design: VisualDesign = { ...draft, [section]: next };
    await this.writeDraft(context, design, record, [{ area: HISTORY_AREA_OF_SECTION[section], action: "RASCUNHO_SALVO", detail: `${HISTORY_AREA_OF_SECTION[section]} alterado no rascunho.` }]);
    if (section === "theme") await this.themeEvent(context, "STUDIO_THEME_UPDATED", design.theme.themeId, null, draft.theme, design.theme);
    return this.getStudio(context);
  }

  /** Envia logo / imagem de fundo pelo armazenamento oficial de arquivos; o desenho guarda só a referência. */
  async uploadAsset(context: RequestContext, target: StudioAssetTarget, dataUrl: string, fileName: string, expectedDraftVersion: number): Promise<StudioView> {
    const slotInfo = target.kind === "logo" ? LOGO_SLOTS.find(s => s.slot === target.slot) : null;
    if (target.kind === "logo" && !slotInfo) throw new AppError(ErrorCode.Validation, "Unknown slot", { safeMessage: "Tipo de logo desconhecido." });
    this.auth.require(context, target.kind === "logo" ? PermissionCode.StudioIdentityUpdate : PermissionCode.StudioLoginUpdate);
    const rules = this.assetRules(target);
    const { draft, record } = await this.draftForWrite(expectedDraftVersion);
    const asset = await this.storeImage(context, dataUrl, fileName, rules);
    const design = this.withAsset(draft, target, asset);
    await this.writeDraft(context, design, record, [{ area: target.kind === "logo" ? "Logo" : "Login", action: "ARQUIVO_ENVIADO", detail: `${rules.label}: ${asset.fileName} (${(asset.size / 1024).toFixed(0)} KB).` }]);
    return this.getStudio(context);
  }

  /** RESTAURAR PADRÃO de um logo / da imagem de fundo (no rascunho). O arquivo antigo não é apagado: versões publicadas podem usá-lo. */
  async clearAsset(context: RequestContext, target: StudioAssetTarget, expectedDraftVersion: number): Promise<StudioView> {
    this.auth.require(context, target.kind === "logo" ? PermissionCode.StudioIdentityUpdate : PermissionCode.StudioLoginUpdate);
    const { draft, record } = await this.draftForWrite(expectedDraftVersion);
    const design = this.withAsset(draft, target, null);
    const label = this.assetRules(target).label;
    await this.writeDraft(context, design, record, [{ area: target.kind === "logo" ? "Logo" : "Login", action: "RASCUNHO_SALVO", detail: `${label}: restaurado o padrão INSPECT IA.` }]);
    return this.getStudio(context);
  }

  async discardDraft(context: RequestContext, expectedDraftVersion: number): Promise<StudioView> {
    if (!ALL_SECTIONS.some(s => this.auth.can(context, SECTION_PERMISSIONS[s].update))) {
      throw new AppError(ErrorCode.Authorization, "No studio update permission", { safeMessage: "Você não tem permissão de edição no Studio." });
    }
    const { record } = await this.draftForWrite(expectedDraftVersion);
    const { design: published } = await this.provider.published();
    await this.writeDraft(context, published, record, [{ area: "Rascunho", action: "RASCUNHO_DESCARTADO", detail: "Rascunho descartado — voltou a ser igual à versão publicada." }]);
    return this.getStudio(context);
  }

  // ═══════════════════════════════════════════════ publicação e restauração

  async publish(context: RequestContext, reason: string, expectedDraftVersion: number): Promise<StudioView> {
    this.auth.require(context, PermissionCode.StudioPublish);
    const cleanReason = this.requireReason(reason);
    const { draft, record } = await this.draftForWrite(expectedDraftVersion);
    const errors = validateForPublish(draft);
    if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Incomplete design", { safeMessage: `Não é possível publicar uma alteração incompleta: ${errors.join(" ")}` });
    const { design: published, version: current } = await this.provider.published();
    const changed = changedSections(draft, published);
    if (changed.length === 0) throw new AppError(ErrorCode.Validation, "Nothing to publish", { safeMessage: "O rascunho é igual à versão publicada — não há o que publicar." });
    const version = current + 1;
    await this.appendVersion(context, version, draft, cleanReason, null);
    await this.writeDraft(context, draft, record, [], version);
    await this.recordHistory(context, [
      ...changed.map(s => ({ area: HISTORY_AREA_OF_SECTION[s], action: "PUBLICADO" as const, detail: s === "templates" ? `Template publicado (V${version}).` : `${HISTORY_AREA_OF_SECTION[s]} alterado (V${version}).` })),
      { area: "Publicação", action: "PUBLICADO", detail: `Versão V${version} publicada: ${cleanReason}` }
    ], version);
    await this.auditEvent(context, "STUDIO_DESIGN_PUBLISHED", { version, changed, reason: cleanReason });
    if (changed.includes("theme")) {
      await this.themeEvent(context, "STUDIO_THEME_PUBLISHED", draft.theme.themeId, version, published.theme, draft.theme);
      await this.themeEvent(context, "STUDIO_THEME_ACTIVATED", draft.theme.themeId, version, published.theme, draft.theme);
    }
    return this.getStudio(context);
  }

  /**
   * RESTAURAR Vn — cria uma versão NOVA com o conteúdo de Vn. Exige a
   * confirmação digitada ("RESTAURAR V3") e rascunho sem pendência: um
   * rascunho em andamento seria perdido sem a pessoa decidir.
   */
  async restoreVersion(context: RequestContext, version: number, reason: string, confirmation: string, expectedDraftVersion: number): Promise<StudioView> {
    this.auth.require(context, PermissionCode.StudioPublish);
    const cleanReason = this.requireReason(reason);
    if (String(confirmation ?? "").trim().toUpperCase() !== `RESTAURAR V${version}`) {
      throw new AppError(ErrorCode.Validation, "Confirmation required", { safeMessage: `Para restaurar, digite exatamente: RESTAURAR V${version}` });
    }
    const { draft, record } = await this.draftForWrite(expectedDraftVersion);
    const { design: published, version: current } = await this.provider.published();
    if (changedSections(draft, published).length > 0) throw new AppError(ErrorCode.Validation, "Draft pending", { safeMessage: "Há alterações não publicadas no rascunho. Publique ou descarte o rascunho antes de restaurar uma versão." });
    if (version === current) throw new AppError(ErrorCode.Validation, "Already current", { safeMessage: `V${version} já é a versão vigente.` });
    const target = version === 0 ? defaultVisualDesign() : (await this.repo.getVersion(version)) ? normalizeVisualDesign((await this.repo.getVersion(version))!.payload) : null;
    if (!target) throw new AppError(ErrorCode.NotFound, "Version not found", { safeMessage: `Versão V${version} não encontrada.` });
    const next = current + 1;
    await this.appendVersion(context, next, target, `Restauração de V${version}: ${cleanReason}`, version);
    await this.writeDraft(context, target, record, [], next);
    await this.recordHistory(context, [{ area: "Publicação", action: "RESTAURADO", detail: `V${version} restaurada como V${next}: ${cleanReason}` }], next);
    await this.auditEvent(context, "STUDIO_DESIGN_RESTORED", { restoredFrom: version, version: next, reason: cleanReason });
    if (JSON.stringify(target.theme) !== JSON.stringify(published.theme)) {
      await this.themeEvent(context, "STUDIO_THEME_ROLLED_BACK", target.theme.themeId, next, published.theme, target.theme);
    }
    return this.getStudio(context);
  }

  // ═══════════════════════════════════════════════════════════ internos

  private loginAppearanceOf = async (design: VisualDesign, version: number): Promise<LoginAppearance> => {
    const { backgroundImage, brandLogo, ...login } = design.login;
    return {
      version, login,
      backgroundImageUrl: await this.provider.resolveAsset(backgroundImage),
      brandLogoUrl: await this.provider.resolveAsset(brandLogo),
      loginLogoUrl: await this.provider.resolveAsset(design.logos.login),
      appLogoUrl: await this.provider.resolveAsset(design.logos.app),
      faviconUrl: await this.provider.resolveAsset(design.logos.favicon),
      appIconUrl: await this.provider.resolveAsset(design.logos.appIcon),
      identity: { subtitle: design.identity.subtitle, versionLabel: design.identity.versionLabel },
      fontStack: (FONT_OPTIONS.find(f => f.id === design.typography.bodyFont) ?? FONT_OPTIONS[0]!).stack
    };
  };

  private assetRules(target: StudioAssetTarget): { label: string; mimeTypes: readonly string[]; maxBytes: number } {
    if (target.kind === "logo") {
      const slot = LOGO_SLOTS.find(s => s.slot === target.slot);
      if (!slot) throw new AppError(ErrorCode.Validation, "Unknown slot", { safeMessage: "Tipo de logo desconhecido." });
      return { label: slot.label, mimeTypes: slot.mimeTypes, maxBytes: slot.maxBytes };
    }
    if (target.kind === "loginBrandLogo") return { label: "Logo da marca no login", mimeTypes: ["image/png", "image/jpeg", "image/webp"], maxBytes: 1_000_000 };
    return { label: "Imagem de fundo do login", mimeTypes: ["image/png", "image/jpeg", "image/webp"], maxBytes: 3_000_000 };
  }

  private withAsset(draft: VisualDesign, target: StudioAssetTarget, asset: StudioAsset | null): VisualDesign {
    if (target.kind === "logo") return { ...draft, logos: { ...draft.logos, [target.slot]: asset } };
    if (target.kind === "loginBrandLogo") return { ...draft, login: { ...draft.login, brandLogo: asset } };
    return { ...draft, login: { ...draft.login, backgroundImage: asset } };
  }

  /** Valida tipo, conteúdo real (bytes iniciais) e tamanho; guarda no armazenamento oficial. SVG nunca. */
  private async storeImage(context: RequestContext, dataUrl: string, fileName: string, rules: { label: string; mimeTypes: readonly string[]; maxBytes: number }): Promise<StudioAsset> {
    const match = DATA_URL.exec(String(dataUrl ?? ""));
    if (!match) throw new AppError(ErrorCode.Validation, "Invalid image", { safeMessage: "Envie uma imagem PNG, JPEG ou WEBP. SVG não é aceito." });
    const mimeType = match[1]!;
    const base64 = match[2]!;
    if (!rules.mimeTypes.includes(mimeType)) throw new AppError(ErrorCode.Validation, "Mime not allowed", { safeMessage: `Tipo ${mimeType} não aceito para ${rules.label} (${rules.mimeTypes.join(", ")}).` });
    if (!base64.startsWith(MAGIC[mimeType] ?? "?")) throw new AppError(ErrorCode.Validation, "Content mismatch", { safeMessage: "O conteúdo do arquivo não corresponde ao tipo de imagem informado." });
    const size = Math.floor((base64.length * 3) / 4);
    if (size > rules.maxBytes) throw new AppError(ErrorCode.Validation, "Image too large", { safeMessage: `A imagem tem ${(size / 1024).toFixed(0)} KB — o limite é ${(rules.maxBytes / 1024).toFixed(0)} KB.` });
    const cleanName = String(fileName ?? "imagem").replace(/[^\w.\- ]+/g, "_").slice(0, 120) || "imagem";
    const ref = await this.storage.store(dataUrl);
    return { ref, fileName: cleanName, mimeType, size, sha256: await sha256Hex(dataUrl), uploadedAt: new Date().toISOString(), uploadedBy: context.userId || "system" };
  }

  private async draftDesign(): Promise<VisualDesign> {
    const record = await this.repo.loadDraft();
    return record ? normalizeVisualDesign(record.payload) : (await this.provider.published()).design;
  }

  private async draftForWrite(expectedDraftVersion: number): Promise<{ draft: VisualDesign; record: { version: number } }> {
    const record = await this.repo.loadDraft();
    const version = record?.version ?? 0;
    if (!Number.isInteger(expectedDraftVersion) || expectedDraftVersion !== version) {
      throw new AppError(ErrorCode.Conflict, "Draft version conflict", { safeMessage: "O rascunho do Studio foi alterado por outra pessoa depois que você abriu a tela. Recarregue antes de continuar." });
    }
    return { draft: record ? normalizeVisualDesign(record.payload) : (await this.provider.published()).design, record: { version } };
  }

  private async writeDraft(context: RequestContext, design: VisualDesign, record: { version: number }, history: Array<Pick<StudioHistoryEntry, "area" | "action" | "detail">>, baseVersion?: number): Promise<void> {
    const base = baseVersion ?? (await this.repo.loadDraft())?.baseVersion ?? (await this.provider.published()).version;
    const ok = await this.repo.saveDraftIfVersion(design, base, record.version, { updatedAt: new Date().toISOString(), updatedBy: context.userId || "system" });
    if (!ok) throw new AppError(ErrorCode.Conflict, "Draft version conflict", { safeMessage: "O rascunho do Studio foi alterado por outra pessoa. Recarregue antes de continuar." });
    if (history.length > 0) await this.recordHistory(context, history, null);
  }

  private async appendVersion(context: RequestContext, version: number, design: VisualDesign, reason: string, restoredFrom: number | null): Promise<void> {
    const userId = context.userId || "system";
    const ok = await this.repo.appendVersion({
      version, payload: design, publishedAt: new Date().toISOString(), publishedBy: userId,
      publishedByName: (this.userNameOf ? await this.userNameOf(userId).catch(() => null) : null) || userId, reason, restoredFrom
    });
    this.provider.invalidate();
    if (!ok) throw new AppError(ErrorCode.Conflict, "Version conflict", { safeMessage: "Outra publicação aconteceu ao mesmo tempo. Recarregue e publique de novo." });
  }

  private async recordHistory(context: RequestContext, items: Array<Pick<StudioHistoryEntry, "area" | "action" | "detail">>, version: number | null): Promise<void> {
    const userId = context.userId || "system";
    const userName = (this.userNameOf ? await this.userNameOf(userId).catch(() => null) : null) || userId;
    const at = new Date().toISOString();
    await this.repo.appendHistory(items.map(i => ({ id: randomUUID(), at, userId, userName, area: i.area, action: i.action, detail: i.detail.slice(0, 500), version })));
  }

  private requireReason(reason: string): string {
    const clean = String(reason ?? "").trim();
    if (clean.length < 5) throw new AppError(ErrorCode.Validation, "Reason required", { safeMessage: "Informe o motivo (mínimo 5 caracteres) — ele fica registrado na versão e no histórico." });
    return clean.slice(0, 300);
  }

  private async auditEvent(context: RequestContext, event: string, metadata: Record<string, unknown>): Promise<void> {
    await this.audit?.record(context, {
      action: AuditAction.Update, entityType: "StudioDesign", entityId: "GLOBAL", previousState: null, newState: null,
      metadata: { event, ...metadata } as unknown as JsonObject
    }).catch(() => null);
  }
}
